/* eslint-disable no-param-reassign */
import { createSlice } from '@reduxjs/toolkit';
import arClient from '../../util/api_client';
import { OK, LEGACY_OK } from '../../constants/platformStatusCodes';
import { mapProducts } from './utils/utils';

// Cache for API results
const apiCache = new Map();

// Helper function to generate cache key
const getCacheKey = (functionName, requestId, platform, indexes = null) => {
  const normalizedPlatform = Array.isArray(platform) ? platform.join('_') : platform;
  const indexesStr =
    indexes && Array.isArray(indexes) && indexes.length > 0 ? JSON.stringify(indexes.sort()) : '';
  return `${functionName}_${requestId}_${normalizedPlatform}_${indexesStr}`;
};

const initialState = {
  competitiveIntelList: [],
  competitiveIntelListLoading: false,
  overviewDetails: null,
  overviewDetailsLoading: false,
  socialNetworks: [],
  socialNetworksLoading: false,
  socialNetworksAccessDenied: false,
  summary: null,
  summaryLoading: false,
  posts: null,
  postsLoading: false,
  metrics: null,
  metricsLoading: false,
  metricProducts: [],
  summaryProducts: [],
  buyingMotivationsCategories: [],
  emotionCategories: [],
  sentimentCategories: [],
  painpointsCategories: [],
  productNames: [],
  selectedPlatform: [],
};

const CompetitiveIntelSlice = createSlice({
  name: 'CompetitiveIntel',
  initialState,
  reducers: {
    setCompetitiveIntelList(state, action) {
      state.competitiveIntelList = action.payload;
    },
    setCompetitiveIntelListLoading(state, action) {
      state.competitiveIntelListLoading = action.payload;
    },
    setOverviewDetails(state, action) {
      state.overviewDetails = action.payload;
    },
    setOverviewDetailsLoading(state, action) {
      state.overviewDetailsLoading = action.payload;
    },
    setSocialNetworks(state, action) {
      state.socialNetworks = action.payload;
    },
    setSocialNetworksLoading(state, action) {
      state.socialNetworksLoading = action.payload;
    },
    setSocialNetworksAccessDenied(state, action) {
      state.socialNetworksAccessDenied = action.payload;
    },
    setSummary(state, action) {
      state.summary = action.payload;
    },
    setSummaryLoading(state, action) {
      state.summaryLoading = action.payload;
    },
    setPosts(state, action) {
      state.posts = action.payload;
    },
    setPostsLoading(state, action) {
      state.postsLoading = action.payload;
    },
    setMetrics(state, action) {
      state.metrics = action.payload;
    },
    setMetricsLoading(state, action) {
      state.metricsLoading = action.payload;
    },
    setMetricProducts(state, action) {
      state.metricProducts = action.payload;
    },
    setSummaryProducts(state, action) {
      state.summaryProducts = action.payload;
    },
    setBuyingMotivationsCategories(state, action) {
      state.buyingMotivationsCategories = action.payload;
    },
    setEmotionCategories(state, action) {
      state.emotionCategories = action.payload;
    },
    setSentimentCategories(state, action) {
      state.sentimentCategories = action.payload;
    },
    setPainPointsCategories(state, action) {
      state.painPointsCategories = action.payload;
    },
    setProductNames(state, action) {
      state.productNames = action.payload;
    },
    setSelectedPlatform(state, action) {
      state.selectedPlatform = action.payload;
    },
  },
});

export const {
  setCompetitiveIntelList,
  setCompetitiveIntelListLoading,
  setOverviewDetails,
  setOverviewDetailsLoading,
  setSocialNetworks,
  setSocialNetworksLoading,
  setSocialNetworksAccessDenied,
  setSummary,
  setSummaryLoading,
  setPosts,
  setPostsLoading,
  setMetrics,
  setMetricsLoading,
  setMetricProducts,
  setSummaryProducts,
  setBuyingMotivationsCategories,
  setEmotionCategories,
  setSentimentCategories,
  setPainPointsCategories,
  setProductNames,
  setSelectedPlatform,
} = CompetitiveIntelSlice.actions;

export const getCompetitiveIntelList = () => (dispatch) => {
  return new Promise((resolve, reject) => {
    dispatch(setCompetitiveIntelListLoading(true));
    const callback = (resp) => {
      if (resp.isCancelled) {
        dispatch(setCompetitiveIntelListLoading(false));
        resolve(null);
        return;
      }
      if (
        resp.status === LEGACY_OK ||
        resp.status === OK ||
        resp.status === 200 ||
        resp.status === 'INVALID_DATA'
      ) {
        try {
          dispatch(setCompetitiveIntelListLoading(false));
          const list = resp?.data?.requests || [];
          dispatch(setCompetitiveIntelList(list));
          resolve(resp);
        } catch (e) {
          console.error('error loading list: ', e);
        }
      } else {
        dispatch(setCompetitiveIntelListLoading(false));
        reject(new Error(resp?.error || resp?.message || 'API request failed'));
      }
    };

    arClient.doApiCallGet('/competitive-intel/', callback);
  });
};

export const findCompetitors = (requestId) => {
  return new Promise((resolve, reject) => {
    const callback = (resp) => {
      if (resp.isCancelled) {
        resolve(null);
        return;
      }
      if (
        resp.status === LEGACY_OK ||
        resp.status === OK ||
        resp.status === 200 ||
        resp.status === 'INVALID_DATA'
      ) {
        try {
          resolve(resp);
        } catch (e) {
          console.error('error finding competitors: ', e);
          reject(e);
        }
      } else {
        reject(new Error(resp?.error || resp?.message || 'API request failed'));
      }
    };

    arClient.doApiCallGet(`/competitive-intel/${requestId}/competitors?status=all`, callback);
  });
};

export const renameCompetitiveIntel = (requestId, productName) => (dispatch, getState) => {
  return new Promise((resolve, reject) => {
    const callback = (resp) => {
      if (resp.isCancelled) {
        resolve(null);
        return;
      }
      if (resp.status === LEGACY_OK || resp.status === OK || resp.status === 200) {
        try {
          const updated = resp?.data;
          if (updated && typeof updated === 'object') {
            const list = getState().CompetitiveIntel.competitiveIntelList || [];
            const next = list.map((item) =>
              String(item.id) === String(requestId) || String(item.id) === String(updated.id)
                ? { ...item, ...updated }
                : item
            );
            dispatch(setCompetitiveIntelList(next));
          }
          resolve(resp);
        } catch (e) {
          console.error('error renaming competitive intel: ', e);
          reject(e);
        }
      } else {
        reject(
          new Error(
            resp?.message || resp?.error || resp?.error_description || 'Could not rename report'
          )
        );
      }
    };

    arClient.doApiCallV2(`/competitive-intel/${requestId}/rename`, { productName }, callback);
  });
};

export const updateCompetitiveIntel = (productName, productUrl, requestId) => {
  return new Promise((resolve, reject) => {
    const callback = (resp) => {
      if (resp.isCancelled) {
        resolve(null);
        return;
      }
      if (
        resp.status === LEGACY_OK ||
        resp.status === OK ||
        resp.status === 200 ||
        resp.status === 'INVALID_DATA'
      ) {
        try {
          resolve(resp);
        } catch (e) {
          console.error('error creating competitive intel: ', e);
        }
      } else {
        reject(new Error(resp?.error || resp?.message || 'API request failed'));
      }
    };

    arClient.doApiCall(
      `/competitive-intel/${requestId}`,
      { productName, productUrl },
      callback,
      null,
      { timeout: 300000 }
    );
  });
};

export const createCompetitiveIntel = (productName, productUrl) => {
  return new Promise((resolve, reject) => {
    const callback = (resp) => {
      if (resp.isCancelled) {
        resolve(null);
        return;
      }
      if (
        resp.status === LEGACY_OK ||
        resp.status === OK ||
        resp.status === 200 ||
        resp.status === 'INVALID_DATA'
      ) {
        try {
          resolve(resp);
        } catch (e) {
          console.error('error creating competitive intel: ', e);
        }
      } else {
        reject(new Error(resp?.error || resp?.message || 'API request failed'));
      }
    };

    arClient.doApiCall('/competitive-intel/', { productName, productUrl }, callback, null, {
      timeout: 300000,
    });
  });
};

export const checkStatus = (requestId) => {
  return new Promise((resolve, reject) => {
    const callback = (resp) => {
      if (resp.isCancelled) {
        resolve(null);
        return;
      }
      if (
        resp.status === LEGACY_OK ||
        resp.status === OK ||
        resp.status === 200 ||
        resp.status === 'INVALID_DATA'
      ) {
        try {
          resolve(resp);
        } catch (e) {
          console.error('error checking status: ', e);
          reject(e);
        }
      } else {
        reject(new Error(resp?.error || resp?.message || 'API request failed'));
      }
    };

    arClient.doApiCallGet(`/competitive-intel/${requestId}/processing-status`, callback);
  });
};

export const getValidatedProduct = (requestId) => {
  return new Promise((resolve, reject) => {
    const callback = (resp) => {
      if (
        resp.status === LEGACY_OK ||
        resp.status === OK ||
        resp.status === 200 ||
        resp.status === 'INVALID_DATA'
      ) {
        try {
          resolve(resp);
        } catch (e) {
          console.error('error getting validated product: ', e);
          reject(e);
        }
      } else {
        reject(new Error(resp?.error || resp?.message || 'API request failed'));
      }
    };

    arClient.doApiCallGet(`/competitive-intel/${requestId}/validated-product`, callback);
  });
};

export const confirmValidationProduct = (requestId, selectedProductType) => {
  return new Promise((resolve, reject) => {
    const callback = (resp) => {
      if (
        resp.status === LEGACY_OK ||
        resp.status === OK ||
        resp.status === 200 ||
        resp.status === 'INVALID_DATA'
      ) {
        try {
          resolve(resp);
        } catch (e) {
          console.error('error getting validated product: ', e);
          reject(e);
        }
      } else {
        reject(new Error(resp?.error || resp?.message || 'API request failed'));
      }
    };

    arClient.doApiCall(
      `/competitive-intel/${requestId}/confirm-validation`,
      { selectedProductType },
      callback
    );
  });
};

export const confirmCompetitors = (requestId) => {
  return new Promise((resolve, reject) => {
    const callback = (resp) => {
      if (
        resp.status === LEGACY_OK ||
        resp.status === OK ||
        resp.status === 200 ||
        resp.status === 'INVALID_DATA'
      ) {
        try {
          resolve(resp);
        } catch (e) {
          console.error('error getting validated product: ', e);
          reject(e);
        }
      } else {
        reject(new Error(resp?.error || resp?.message || 'API request failed'));
      }
    };

    arClient.doApiCall(`/competitive-intel/${requestId}/confirm-competitors`, {}, callback);
  });
};

export const updateCompetitor = (competitorId, status) => {
  return new Promise((resolve, reject) => {
    const callback = (resp) => {
      if (
        resp.status === LEGACY_OK ||
        resp.status === OK ||
        resp.status === 200 ||
        resp.status === 'INVALID_DATA'
      ) {
        try {
          resolve(resp);
        } catch (e) {
          console.error('error updating competitor: ', e);
          reject(e);
        }
      } else {
        reject(new Error(resp?.error || resp?.message || 'API request failed'));
      }
    };

    arClient.doApiCall(
      `/competitive-intel/competitors/${competitorId}`,
      { status },
      callback,
      null,
      { method: 'PUT' }
    );
  });
};

export const updateNotificationEmails = (emails, requestId) => {
  return new Promise((resolve, reject) => {
    const callback = (resp) => {
      if (
        resp.status === LEGACY_OK ||
        resp.status === OK ||
        resp.status === 200 ||
        resp.status === 'INVALID_DATA'
      ) {
        resolve(resp);
      } else {
        reject(new Error(resp?.error || resp?.message || 'API request failed'));
      }
    };

    arClient.doApiCall(
      `/competitive-intel/${requestId}/notification-emails`,
      { emails: JSON.stringify(emails) },
      callback
    );
  });
};

export const getCompetitiveIntelOverview = (requestId) => (dispatch) => {
  return new Promise((resolve, reject) => {
    dispatch(setOverviewDetailsLoading(true));
    const callback = (resp) => {
      if (
        resp.status === LEGACY_OK ||
        resp.status === OK ||
        resp.status === 200 ||
        resp.status === 'INVALID_DATA'
      ) {
        dispatch(setOverviewDetails(resp.data));
        dispatch(setOverviewDetailsLoading(false));
        resolve(resp);
      } else {
        dispatch(setOverviewDetailsLoading(false));
        reject(new Error(resp?.error || resp?.message || 'API request failed'));
      }
    };

    arClient.doApiCallGet(`/competitive-intel/${requestId}/overview`, callback);
  });
};

export const getCompetitiveIntelSocialNetworks = (requestId) => (dispatch) => {
  return new Promise((resolve, reject) => {
    const cacheKey = getCacheKey('getCompetitiveIntelSocialNetworks', requestId);

    // Reset access denied state
    dispatch(setSocialNetworksAccessDenied(false));

    // Check cache first
    if (apiCache.has(cacheKey)) {
      const cachedResp = apiCache.get(cacheKey);
      dispatch(setSocialNetworks(cachedResp.data.platforms));
      dispatch(setSocialNetworksLoading(false));
      resolve(cachedResp);
      return;
    }

    dispatch(setSocialNetworksLoading(true));
    const callback = (resp) => {
      try {
        // Check for access denied (FORBIDDEN, NOT_FOUND status, or error message)
        const isAccessDenied =
          resp.status === 'FORBIDDEN' ||
          resp.status === 'NOT_FOUND' ||
          resp.status === 404 ||
          resp?.error?.includes('not found') ||
          resp?.message?.includes('not found');

        if (isAccessDenied) {
          dispatch(setSocialNetworksAccessDenied(true));
          dispatch(setSocialNetworksLoading(false));
          dispatch(setSocialNetworks([]));
          reject(new Error(resp?.error || resp?.message || 'Access denied'));
          return;
        }

        if (
          resp.status === LEGACY_OK ||
          resp.status === OK ||
          resp.status === 200 ||
          resp.status === 'INVALID_DATA'
        ) {
          // Store in cache
          apiCache.set(cacheKey, resp);

          dispatch(setSocialNetworks(resp.data?.platforms || []));
          dispatch(setSocialNetworksLoading(false));
          resolve(resp);
        } else {
          // Any other error status
          dispatch(setSocialNetworksAccessDenied(true));
          dispatch(setSocialNetworksLoading(false));
          dispatch(setSocialNetworks([]));
          reject(new Error(resp?.error || resp?.message || 'Request failed'));
        }
      } catch (e) {
        console.error('error getting social networks: ', e);
        dispatch(setSocialNetworksAccessDenied(true));
        dispatch(setSocialNetworksLoading(false));
        dispatch(setSocialNetworks([]));
        reject(e);
      }
    };

    arClient.doApiCallGet(`/competitive-intel/${requestId}/platforms`, callback);
  });
};

export const getCompetitiveIntelSummary = (requestId, platform) => (dispatch) => {
  return new Promise((resolve, reject) => {
    const cacheKey = getCacheKey('getCompetitiveIntelSummary', requestId, platform);

    // Check cache first
    if (apiCache.has(cacheKey)) {
      const cachedResp = apiCache.get(cacheKey);
      const result = cachedResp.data;
      dispatch(setSummary(result));
      dispatch(setSummaryProducts(mapProducts(result.products)));
      dispatch(setSummaryLoading(false));
      dispatch(setBuyingMotivationsCategories(result.buyingMotivationsCategories));
      dispatch(setEmotionCategories(result.emotionCategories));
      dispatch(setSentimentCategories(result.sentimentCategories));
      dispatch(setPainPointsCategories(result.painPointsCategories));
      dispatch(setProductNames(result.productNames));
      resolve(cachedResp);
      return;
    }

    dispatch(setSummaryLoading(true));
    const callback = (resp) => {
      if (
        resp.status === LEGACY_OK ||
        resp.status === OK ||
        resp.status === 200 ||
        resp.status === 'INVALID_DATA'
      ) {
        if (resp?.error) {
          dispatch(setSummaryLoading(false));
          reject(new Error(resp?.error || resp?.message || 'API request failed'));
          return;
        }
        const result = resp.data;
        // const result = summaries;

        // Store in cache
        apiCache.set(cacheKey, resp);

        dispatch(setSummary(result));
        dispatch(setSummaryProducts(mapProducts(result.products)));
        dispatch(setSummaryLoading(false));
        dispatch(setBuyingMotivationsCategories(result.buyingMotivationsCategories));
        dispatch(setEmotionCategories(result.emotionCategories));
        dispatch(setSentimentCategories(result.sentimentCategories));
        dispatch(setPainPointsCategories(result.painPointsCategories));
        dispatch(setProductNames(result.productNames));
        resolve(resp);
      } else {
        dispatch(setSummaryLoading(false));
        reject(new Error(resp?.error || resp?.message || 'API request failed'));
      }
    };

    const platformParam = Array.isArray(platform) ? platform.join('_') : platform;
    arClient.doApiCallGet(
      `/competitive-intel/${requestId}/summaries?platform=${platformParam}`,
      callback
    );
  });
};

export const getCompetitiveIntelMetrics = (requestId, platform) => (dispatch) => {
  return new Promise((resolve, reject) => {
    const cacheKey = getCacheKey('getCompetitiveIntelMetrics', requestId, platform);

    // Check cache first
    if (apiCache.has(cacheKey)) {
      const cachedResp = apiCache.get(cacheKey);
      const result = cachedResp.data;
      dispatch(setMetrics(result));
      dispatch(setMetricProducts(mapProducts(result.products)));
      dispatch(setMetricsLoading(false));
      resolve(cachedResp);
      return;
    }

    dispatch(setMetricsLoading(true));
    const callback = (resp) => {
      if (resp?.error) {
        dispatch(setMetricsLoading(false));
        reject(new Error(resp?.error || resp?.message || 'API request failed'));
        return;
      }

      if (
        resp.status === LEGACY_OK ||
        resp.status === OK ||
        resp.status === 200 ||
        resp.status === 'INVALID_DATA'
      ) {
        const result = resp.data;
        // const result = metrics;

        // Store in cache
        apiCache.set(cacheKey, resp);

        dispatch(setMetrics(result));
        dispatch(setMetricProducts(mapProducts(result.products)));
        dispatch(setMetricsLoading(false));
        resolve(resp);
      } else {
        dispatch(setMetricsLoading(false));
        reject(new Error(resp?.error || resp?.message || 'API request failed'));
      }
    };

    const metricsPlatform = Array.isArray(platform) ? platform.join('_') : platform;
    arClient.doApiCallGet(
      `/competitive-intel/${requestId}/metrics?platform=${metricsPlatform}`,
      callback
    );
  });
};

export const getCompetitiveIntelPosts = (requestId, platform, indexes = []) => (dispatch) => {
  return new Promise((resolve, reject) => {
    const cacheKey = getCacheKey('getCompetitiveIntelPosts', requestId, platform, indexes);

    // Check cache first
    if (apiCache.has(cacheKey)) {
      const cachedResp = apiCache.get(cacheKey);
      const result = cachedResp.data;
      dispatch(setPosts(result));
      dispatch(setPostsLoading(false));
      resolve(cachedResp);
      return;
    }

    dispatch(setPostsLoading(true));
    const callback = (resp) => {
      if (
        resp.status === LEGACY_OK ||
        resp.status === OK ||
        resp.status === 200 ||
        resp.status === 'INVALID_DATA'
      ) {
        const result = resp.data;

        // Store in cache
        apiCache.set(cacheKey, resp);

        dispatch(setPosts(result));
        dispatch(setPostsLoading(false));
        resolve(resp);
      } else {
        dispatch(setPostsLoading(false));
        reject(new Error(resp?.error || resp?.message || 'API request failed'));
      }
    };

    const postsPlatform = Array.isArray(platform) ? platform.join('_') : platform;
    const postsParams = new URLSearchParams({ platform: postsPlatform });
    if (Array.isArray(indexes) && indexes.length > 0) {
      postsParams.append('indexes', JSON.stringify(indexes));
    }
    arClient.doApiCallGet(`/competitive-intel/${requestId}/posts?${postsParams}`, callback);
  });
};

export const getEmailNotificationList = (requestId) => () => {
  return new Promise((resolve, reject) => {
    const callback = (resp) => {
      if (
        resp.status === LEGACY_OK ||
        resp.status === OK ||
        resp.status === 200 ||
        resp.status === 'INVALID_DATA'
      ) {
        try {
          resolve(resp);
        } catch (e) {
          console.error('error loading list: ', e);
        }
      } else {
        reject(new Error(resp?.error || resp?.message || 'API request failed'));
      }
    };

    arClient.doApiCallGet(`/competitive-intel/${requestId}/notification-emails`, callback);
  });
};

export const deleteCompetitiveIntel = (requestId) => (dispatch) => {
  return new Promise((resolve, reject) => {
    dispatch(setCompetitiveIntelListLoading(true));
    const callback = (resp) => {
      if (
        resp.status === LEGACY_OK ||
        resp.status === OK ||
        resp.status === 200 ||
        resp.status === 'INVALID_DATA'
      ) {
        dispatch(setCompetitiveIntelListLoading(false));
        resolve(resp);
      } else {
        dispatch(setCompetitiveIntelListLoading(false));
        reject(new Error(resp?.error || resp?.message || 'API request failed'));
      }
    };

    arClient.doApiCall(`/competitive-intel/${requestId}/archive`, {}, callback);
  });
};

export const addManuallyProductType = (requestId, productType) => {
  return new Promise((resolve, reject) => {
    const callback = (resp) => {
      if (
        resp.status === LEGACY_OK ||
        resp.status === OK ||
        resp.status === 200 ||
        resp.status === 'INVALID_DATA'
      ) {
        resolve(resp);
      } else {
        reject(new Error(resp?.error || resp?.message || 'API request failed'));
      }
    };

    arClient.doApiCall(
      `/competitive-intel/${requestId}/validate-product-type`,
      { productType },
      callback
    );
  });
};

export const addManuallyCompetitor = (requestId, competingProduct) => {
  return new Promise((resolve, reject) => {
    const callback = (resp) => {
      if (
        resp.status === LEGACY_OK ||
        resp.status === OK ||
        resp.status === 200 ||
        resp.status === 'INVALID_DATA'
      ) {
        resolve(resp);
      } else {
        reject(new Error(resp?.error || resp?.message || 'API request failed'));
      }
    };
    arClient.doApiCall(
      `/competitive-intel/${requestId}/validate-competitor`,
      { competingProduct },
      callback
    );
  });
};

export const rerunCompetitiveIntel = (requestId) => {
  return new Promise((resolve, reject) => {
    const callback = (resp) => {
      if (
        resp.status === LEGACY_OK ||
        resp.status === OK ||
        resp.status === 200 ||
        resp.status === 'INVALID_DATA'
      ) {
        resolve(resp);
      } else {
        reject(new Error(resp?.error || resp?.message || 'API request failed'));
      }
    };

    arClient.doApiCall(`/competitive-intel/${requestId}/rerun`, {}, callback);
  });
};

export const cancelCompetitiveIntel = (requestId) => {
  return new Promise((resolve, reject) => {
    const callback = (resp) => {
      if (
        resp.status === LEGACY_OK ||
        resp.status === OK ||
        resp.status === 200 ||
        resp.status === 'INVALID_DATA'
      ) {
        resolve(resp);
      } else {
        reject(new Error(resp?.error || resp?.message || 'API request failed'));
      }
    };
    arClient.doApiCall(`/competitive-intel/${requestId}/cancel`, {}, callback);
  });
};

export default CompetitiveIntelSlice.reducer;
